import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthProvider';
import UserContext from '../../contexts/UserContext';
import { useDealership } from '../../contexts/DealershipContext';
import './OrderSummary.css';
import { fetchOrderStatuses, getStatusIdByLabel } from '../../utils/statusUtils';
import { calculateLineItemTotals } from '../../utils/pricing';

const isCustomAdItem = (item) =>
  item.name === "Custom Ad Request" ||
  item.name === "CustomAdRequest" ||
  item.name?.includes("Custom Ad");

const OrderSummary = ({ cartItems }) => {
  const history = useHistory();
  const {
    lineItems,
    name,
    email,
    phone,
    message,
    clearCart,
    updateOrderDetails
  } = useCart();
  const { dealership, getOrders } = useDealership();
  const { user } = useContext(UserContext);
  const { api } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noteText, setNoteText] = useState('');

  const normalizeLineItems = (items) => items.map((item) => {
    if (isCustomAdItem(item)) {
      return {
        ...item,
        quantity: Number(item.quantity) || 1,
        price: Number(item.price) || 0,
        fee: Number(item.fee) || 0,
        markup: Number(item.markup) || 0,
        total: Number(item.total) || 0,
      };
    }

    const pricing = calculateLineItemTotals({
      price: item.price,
      fee: item.fee,
      quantity: item.quantity,
    });

    return { ...item, ...pricing };
  });

  const itemsSource = cartItems?.length ? cartItems : lineItems;
  const normalizedLineItems = normalizeLineItems(itemsSource);
  const computedTotal = normalizedLineItems.reduce(
    (sum, item) => sum + (Number(item.total) || 0),
    0
  );
  const hasCustomItems = normalizedLineItems.some(isCustomAdItem);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };


  // Initialize note text from cart state when component mounts
  useEffect(() => {
    if (message) {
      setNoteText(message);
    }
  }, [message]);

  // Update message in cart state when note changes
  const handleNoteChange = (e) => {
    const newNote = e.target.value;
    setNoteText(newNote);
    updateOrderDetails({ message: newNote });
  };

  const resolvePlacedStatusId = async () => {
    try {
      const statuses = await fetchOrderStatuses();
      return getStatusIdByLabel(statuses, 'ORDER_RECEIVED');
    } catch (err) {
      return null;
    }
  };

  const createOrder = async () => {
    // Validate required information
    if (!dealership?.bac) {
      setError('No dealership associated with this account.');
      return;
    }
    if (normalizedLineItems.length === 0) {
      setError('Cart is empty. Cannot create order.');
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const placedStatusId = await resolvePlacedStatusId();
      if (!placedStatusId) {
        setError('Unable to determine order status. Please try again.');
        setIsLoading(false);
        return;
      }
      // Prepare order data
      const orderData = {
        name: name || dealership.name,
        bac: dealership.bac,
        // Items added before login are stamped with an empty email, and
        // dealerships have no email field — the signed-in user (required
        // to reach this point) is the reliable fallback.
        email: email || user?.email || dealership.email,
        phone: phone || dealership.salesPhone,
        orderTotal: computedTotal,
        message: noteText || '',
        statusId: placedStatusId,
        dealershipBac: dealership.bac,
        lineItems: normalizedLineItems.map((item) => {
          if (isCustomAdItem(item)) {
            return {
              itemId: item.itemId,
              quantity: item.quantity,
              price: item.price,
              fee: item.fee,
              markup: item.markup,
              total: item.total,
              adType: item.adType,
              adSize: item.adSize,
              numVehicles: item.numVehicles,
              offerType: item.offerType,
            };
          }
          return {
            itemId: item.itemId,
            packageId: item.packageId,
            variantId: item.variantId,
            quantity: item.quantity,
            price: item.price,
            fee: item.fee,
            markup: item.markup,
            total: item.total,
            employeeId: item.employeeId,
          };
        }),
        include: { lineItems: true }
      };
      // Send order to backend
      await api.post('/api/createOrder', orderData);
      // Refresh orders so the new order appears immediately
      await getOrders();
      // Clear cart after successful order
      clearCart();
      // Redirect to order history page
      history.push('/orders');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create order');
      setIsLoading(false);
    }
  };

  return (
    <div className="order-summary">
      <h2>Order Summary</h2>
      <p className="summary-count">
        {normalizedLineItems.length} {normalizedLineItems.length === 1 ? 'item' : 'items'}
      </p>

      {error && (
        <div className="error-message" style={{ color: 'red', marginBottom: '10px' }}>
          {error}
        </div>
      )}

      <ul className="order-items">
        {normalizedLineItems.map((item, index) => (
          <li key={index}>
            <span className="summary-item-name">{item.name}</span>
            <span className="summary-item-price">
              {/* Custom Creative work is quoted by MA — price stays 0 in the data */}
              {isCustomAdItem(item) ? 'Prices Vary' : formatCurrency(item.total)}
            </span>
          </li>
        ))}
      </ul>

      <div className="order-total">
        <span>Total</span>
        <span>
          {hasCustomItems && computedTotal === 0
            ? 'Prices Vary'
            : formatCurrency(computedTotal)}
        </span>
      </div>

      {hasCustomItems && computedTotal > 0 && (
        <p className="custom-pricing-note">
          Total excludes Custom Creative items — MA will contact you with pricing.
        </p>
      )}

      <div className="order-notes">
        <h3>Order Notes</h3>
        <textarea
          className="notes-textarea"
          placeholder="Add notes for your order here..."
          value={noteText}
          onChange={handleNoteChange}
          rows={4}
        />
      </div>

      <button
        className="place-order-button"
        onClick={createOrder}
        disabled={isLoading || normalizedLineItems.length === 0}
      >
        {isLoading ? 'Placing Order...' : 'Place Order'}
      </button>

    </div>
  );
};

export default OrderSummary;
